import React, { useState, useEffect } from 'react';
import {
  Shield, AlertTriangle, Lock, Globe, Server, Key, Bug,
  TrendingUp, TrendingDown, Info, ChevronDown, ChevronUp,
  CheckCircle, XCircle, AlertCircle, Wifi, Database
} from 'lucide-react';
import MarkdownRenderer from './common/MarkdownRenderer';

/**
 * CyberScorePanel - Advanced security score visualization
 * Displays detailed security metrics from the CyberScore API
 */
const CyberScorePanel = ({ scanId, authToken, onScoreLoaded }) => {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState({});

  useEffect(() => {
    if (scanId) {
      fetchCyberScore();
    }
  }, [scanId]);

  const fetchCyberScore = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/scan/${scanId}/cyberscore`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CyberScore');
      }

      const data = await response.json();
      setScoreData(data);
      if (onScoreLoaded) {
        onScoreLoaded(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': 'from-green-400 to-green-600',
      'A': 'from-green-500 to-green-700',
      'B': 'from-blue-500 to-blue-700',
      'C': 'from-yellow-500 to-yellow-700',
      'D': 'from-orange-500 to-orange-700',
      'E': 'from-red-500 to-red-700',
      'F': 'from-red-600 to-red-800'
    };
    return colors[grade] || colors['C'];
  };

  const getGradeBgClass = (grade) => {
    const colors = {
      'A+': 'bg-green-500/20 border-green-500/50 text-green-400',
      'A': 'bg-green-500/20 border-green-500/50 text-green-400',
      'B': 'bg-blue-500/20 border-blue-500/50 text-blue-400',
      'C': 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
      'D': 'bg-orange-500/20 border-orange-500/50 text-orange-400',
      'E': 'bg-red-500/20 border-red-500/50 text-red-400',
      'F': 'bg-red-500/20 border-red-500/50 text-red-400'
    };
    return colors[grade] || colors['C'];
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'text-red-500 bg-red-500/20',
      'high': 'text-orange-500 bg-orange-500/20',
      'medium': 'text-yellow-500 bg-yellow-500/20',
      'low': 'text-blue-400 bg-blue-500/20',
      'info': 'text-gray-400 bg-gray-500/20'
    };
    return colors[severity] || colors['info'];
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'low':
        return <Info className="w-4 h-4 text-blue-400" />;
      default:
        return <CheckCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getComponentIcon = (componentKey) => {
    const icons = {
      'ssl_tls': Lock,
      'security_headers': Shield,
      'exposed_services': Server,
      'vulnerabilities': Bug,
      'osint_exposure': Globe,
      'web_security': Globe,
      'network_exposure': Wifi,
      'services_security': Server,
      'misconfigurations': AlertTriangle,
      'lateral_movement': TrendingUp,
      'domain_security': Shield,
      'kerberos_security': Key,
      'ldap_security': Database,
      'privilege_escalation': TrendingUp,
      'password_policy': Key,
      'protocol_security': Lock,
      'device_exposure': Server,
      'firmware_security': Database,
      'network_segmentation': Wifi,
      'default_credentials': Key,
      'device_security': Shield,
      'firmware_vulnerabilities': Bug,
      'communication_security': Lock,
      'update_mechanism': TrendingUp
    };
    return icons[componentKey] || Shield;
  };

  const getProbabilityLevel = (prob) => {
    if (prob >= 80) return { label: 'CRITICAL', color: 'text-red-500 border-red-500 bg-red-500/20' };
    if (prob >= 60) return { label: 'HIGH', color: 'text-orange-500 border-orange-500 bg-orange-500/20' };
    if (prob >= 40) return { label: 'MEDIUM', color: 'text-yellow-500 border-yellow-500 bg-yellow-500/20' };
    if (prob >= 20) return { label: 'LOW', color: 'text-blue-400 border-blue-400 bg-blue-500/20' };
    return { label: 'MINIMAL', color: 'text-green-400 border-green-400 bg-green-500/20' };
  };

  const toggleComponent = (key) => {
    setExpandedComponents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-slate-700 h-20 w-20"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-700 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-700 rounded"></div>
              <div className="h-4 bg-slate-700 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 rounded-xl p-6 border border-red-500/50">
        <div className="flex items-center space-x-2 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span>Failed to load CyberScore: {error}</span>
        </div>
      </div>
    );
  }

  if (!scoreData) {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Shield className="w-12 h-12 text-gray-500 mb-4" />
          <p className="text-gray-400 text-sm">Le CyberScore sera disponible une fois les outils de scan executes.</p>
          <p className="text-gray-500 text-xs mt-2">Veuillez patienter pendant l'analyse...</p>
        </div>
      </div>
    );
  }

  const probLevel = getProbabilityLevel(scoreData.hack_probability);

  return (
    <div className="space-y-6">
      {/* Main Score Card */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl p-6 border border-slate-700/50 shadow-xl">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Score Circle */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${getGradeColor(scoreData.grade)} flex items-center justify-center shadow-lg`}>
                <span className="text-4xl font-black text-white drop-shadow-lg">{scoreData.grade}</span>
              </div>
              <div className="absolute -bottom-2 -right-2 bg-slate-800 rounded-full px-2 py-1 border border-slate-600">
                <span className="text-lg font-bold text-white">{scoreData.total_score}</span>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">CyberScore</h3>
              <p className="text-sm text-slate-400">{scoreData.scan_type.replace(/_/g, ' ').toUpperCase()} Scan</p>
              <p className="text-xs text-slate-500 mt-1">{scoreData.tools_analyzed?.length || 0} tools analyzed</p>
            </div>
          </div>

          {/* Hack Probability */}
          <div className="flex flex-col items-center">
            <div className="text-sm text-slate-400 mb-2">Hack Probability</div>
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="40" stroke="#334155" strokeWidth="8" fill="none" />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke={scoreData.hack_probability >= 60 ? '#ef4444' : scoreData.hack_probability >= 40 ? '#f59e0b' : '#10b981'}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${scoreData.hack_probability * 2.51} 251`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-2xl font-bold ${scoreData.hack_probability >= 60 ? 'text-red-500' : scoreData.hack_probability >= 40 ? 'text-yellow-500' : 'text-green-400'}`}>
                  {scoreData.hack_probability}%
                </span>
              </div>
            </div>
            <span className={`mt-2 px-2 py-1 rounded text-xs font-medium border ${probLevel.color}`}>
              {probLevel.label}
            </span>
          </div>
        </div>

        {/* Summary */}
        {scoreData.summary && (
          <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <MarkdownRenderer content={scoreData.summary} variant="compact" />
          </div>
        )}
      </div>

      {/* Component Breakdown */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-4 border-b border-slate-700/50">
          <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            <span>Security Components</span>
          </h4>
        </div>
        <div className="divide-y divide-slate-700/50">
          {Object.entries(scoreData.components || {}).map(([key, component]) => {
            const IconComponent = getComponentIcon(key);
            const isExpanded = expandedComponents[key];

            return (
              <div key={key} className="bg-slate-800/30">
                {/* Component Header */}
                <button
                  onClick={() => toggleComponent(key)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${getSeverityColor(component.severity)}`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-white">{component.name}</div>
                      <div className="text-xs text-slate-400">
                        Weight: {Math.round(component.weight * 100)}% | Issues: {component.issues_count}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Score Bar */}
                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          component.score >= 80 ? 'bg-green-500' :
                          component.score >= 60 ? 'bg-blue-500' :
                          component.score >= 40 ? 'bg-yellow-500' :
                          component.score >= 20 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${component.score}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-white w-8">{component.score}</span>
                    {getSeverityIcon(component.severity)}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && component.findings && component.findings.length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="ml-11 space-y-1">
                      {component.findings.map((finding, idx) => {
                        // Check if this is a sub-item (starts with arrow or spaces)
                        const isSubItem = finding.trim().startsWith('→') || finding.startsWith('  ');

                        if (isSubItem) {
                          return (
                            <div
                              key={idx}
                              className="flex items-start gap-2 text-sm pl-6 py-1"
                            >
                              <span className="text-brand-green">→</span>
                              <span className="text-gray-400 font-mono text-xs break-all">
                                {finding.replace(/^\s*→\s*/, '').trim()}
                              </span>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={idx}
                            className={`flex items-start space-x-2 text-sm p-2 bg-slate-900/50 rounded border border-slate-700/50 ${idx > 0 ? 'mt-2' : ''}`}
                          >
                            <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-300">{finding}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {scoreData.recommendations && scoreData.recommendations.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-4 border-b border-slate-700/50">
            <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span>Recommendations</span>
            </h4>
          </div>
          <div className="p-4 space-y-3">
            {scoreData.recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="flex items-start space-x-3 p-3 bg-green-500/10 rounded-lg border border-green-500/30"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-green-400">{idx + 1}</span>
                </div>
                <p className="text-sm text-slate-300">{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tools Analyzed */}
      {scoreData.tools_analyzed && scoreData.tools_analyzed.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <h4 className="text-sm font-medium text-slate-400 mb-3">Tools Analyzed</h4>
          <div className="flex flex-wrap gap-2">
            {scoreData.tools_analyzed.map((tool, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs bg-slate-700/50 text-slate-300 rounded border border-slate-600/50"
              >
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CyberScorePanel;
